import { reportSentryError } from 'lib/sentry'
import {
  extractTokenFromClientSession,
  SessionAuthenticated,
  SessionUnauthenticated
} from 'modules/customer'
import { apiService, createRequestHeadersConfig } from 'shared/api'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import { Cart, CartOperation, CartResponseTypes } from '../types'

const api = apiService()

export const processBatchedOperations = async (
  operations: { [key: string]: CartOperation },
  cart: Cart | null,
  session: SessionAuthenticated | SessionUnauthenticated,
  callbacks: {
    setCart: (cart: Cart | null) => void
    setOptimisticCart: (cart: Cart | null) => void
    addProductToProcess: (productId: string) => void
    removeProductFromProcess: (productId: string) => void
    trackOperation: (operationId: string, isPending: boolean) => void
  },
  pendingOperations: { [key: string]: CartOperation },
  batchId: number,
  batchIdRef: React.MutableRefObject<number>,
  lastProcessedBatchIdRef: React.MutableRefObject<number>
) => {
  const operationsCount = Object.keys(operations).length

  if (operationsCount === 0) return

  const {
    setCart,
    setOptimisticCart,
    addProductToProcess,
    removeProductFromProcess,
    trackOperation
  } = callbacks

  const operationsByProduct: Record<string, CartOperation[]> = {}

  Object.values(operations).forEach(op => {
    if (!operationsByProduct[op.productId]) {
      operationsByProduct[op.productId] = []
    }
    operationsByProduct[op.productId].push(op)
  })

  const processedOperations: CartOperation[] = []

  Object.entries(operationsByProduct).forEach(([productId, ops]) => {
    const addOps = ops.filter(op => op.type === 'add')
    const updateOps = ops.filter(op => op.type === 'update' && op.itemId)

    if (addOps.length > 0 && updateOps.length > 0) {
      const highestUpdateOp = updateOps.reduce(
        (prev, current) => (current.quantity > prev.quantity ? current : prev),
        updateOps[0]
      )

      const combinedOp: CartOperation = {
        type: 'add',
        productId,
        quantity: highestUpdateOp.quantity,
        product: addOps[0].product
      }

      processedOperations.push(combinedOp)
    } else {
      processedOperations.push(...ops)
    }
  })

  const updateOperations = processedOperations.filter(
    op => op.type === 'update' && op.itemId
  )

  const addOperations = processedOperations.filter(op => op.type === 'add')

  const token = extractTokenFromClientSession(session)

  const promises: Promise<CartResponseTypes | null>[] = []

  if (addOperations.length > 0 || updateOperations.length > 0) {
    const allProductIds = [
      ...addOperations.map(op => op.productId),
      ...updateOperations.map(op => op.productId)
    ]

    allProductIds.forEach(productId => addProductToProcess(productId))
    trackOperation(`batch-update-${Date.now()}`, true)

    if (addOperations.length > 0) {
      const items = addOperations.map(op => ({
        productId: op.productId,
        quantity: op.quantity,
        product: op.product
      }))

      promises.push(
        api.post(
          `/cart/${cart?.id || ''}/add-items`,
          { items },
          createRequestHeadersConfig(token)
        )
      )
    }

    updateOperations.forEach(op => {
      if (!op.itemId) return
      const isTemp = op.itemId.includes('temp-')

      const itemId = isTemp
        ? cart?.items.find(item => item.product.id === op.productId)?.id
        : op.itemId

      if (!itemId) return console.error('Item ID not found', op)

      promises.push(
        api.patch(
          `/cart/${itemId}`,
          { quantity: op.quantity },
          createRequestHeadersConfig(token)
        )
      )
    })

    try {
      const results = await Promise.all(promises)
      const lastResult = results[results.length - 1]

      if (lastResult) {
        setCart(lastResult.data.data)
      }

      if (
        lastResult &&
        batchId >= lastProcessedBatchIdRef.current &&
        batchId === batchIdRef.current &&
        Object.keys(pendingOperations).length === 0
      ) {
        lastProcessedBatchIdRef.current = batchId
        setCart(lastResult.data.data)
        setOptimisticCart(null)
      }
    } catch (err) {
      reportSentryError(err, {
        context: 'Failed to process batched operations',
        extra: {
          operations,
          cart,
          callbacks,
          pendingOperations,
          batchId,
          batchIdRef,
          lastProcessedBatchIdRef
        }
      })
      setOptimisticCart(null)
      toast.error(getApiErrors(err))
    } finally {
      allProductIds.forEach(productId => removeProductFromProcess(productId))
      trackOperation(`batch-update-${Date.now()}`, false)
    }
  }
}
